/**
 * BarrierBoss Cart Error Monitor
 * Fires GA4 custom events on cart API failures and JS errors in cart context.
 * Standalone — no dependencies on volume pricing, shipping estimator, or theme JS.
 *
 * Events sent to GA4:
 *   cart_error        — /cart/add, /cart/change, /cart/update failure
 *   cart_js_error     — uncaught JS error near cart operations
 *   variant_error     — variant selection issues
 *
 * 2026-04-12
 */
(function () {
  'use strict';

  // Wrap fetch to intercept cart API failures
  var _origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var opts = arguments[1];
    if (typeof url === 'string' && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1)) {
      var endpoint = url.indexOf('/cart/add') !== -1 ? 'add' : url.indexOf('/cart/change') !== -1 ? 'change' : 'update';
      return _origFetch.apply(this, arguments).then(function (res) {
        // ⭐ A 200 is NOT success. Every cart bug found on 2026-08-13 returned 200 and rendered
        // wrong: the add committed while the header kept the pre-add total, so the shopper
        // re-clicked and got duplicate lines. Nothing here saw it, because nothing looked past
        // the status code. Re-read the authoritative cart and compare it to what is on screen.
        if (res.ok && endpoint === 'add') {
          setTimeout(function () {
            _origFetch('/cart.js', { credentials: 'same-origin' })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (cart) {
                if (!cart) return;
                var el = document.querySelector('.header_cart_count');
                if (!el) return;
                var shown = parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10);
                if (isNaN(shown) || shown === cart.item_count) return;
                _sendEvent('cart_desync', {
                  endpoint: 'add',
                  status: 200,
                  message: 'header shows ' + shown + ' but cart holds ' + cart.item_count,
                  serverCount: cart.item_count,
                  shownCount: shown,
                  page: location.pathname
                });
              })
              .catch(function () {});
          }, 2500);   // after the drawer re-render and its reconcile pass have had their chance
        }
        if (!res.ok) {
          res.clone().text().then(function (body) {
            _sendEvent('cart_error', {
              endpoint: endpoint,
              status: res.status,
              message: body.substring(0, 200),
              page: location.pathname
            });
          });
        }
        return res;
      }).catch(function (err) {
        _sendEvent('cart_error', {
          endpoint: endpoint,
          status: 0,
          message: err.message || 'network_error',
          page: location.pathname
        });
        throw err;
      });
    }
    return _origFetch.apply(this, arguments);
  };

  // Wrap XMLHttpRequest for themes that use XHR instead of fetch
  var _origXHROpen = XMLHttpRequest.prototype.open;
  var _origXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._bbUrl = url;
    this._bbMethod = method;
    return _origXHROpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var self = this;
    var url = self._bbUrl || '';
    if (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1) {
      var endpoint = url.indexOf('/cart/add') !== -1 ? 'add' : url.indexOf('/cart/change') !== -1 ? 'change' : 'update';
      self.addEventListener('load', function () {
        if (self.status >= 400) {
          _sendEvent('cart_error', {
            endpoint: endpoint,
            status: self.status,
            message: (self.responseText || '').substring(0, 200),
            page: location.pathname
          });
        }
      });
      self.addEventListener('error', function () {
        _sendEvent('cart_error', {
          endpoint: endpoint,
          status: 0,
          message: 'xhr_network_error',
          page: location.pathname
        });
      });
    }
    return _origXHRSend.apply(this, arguments);
  };

  // Catch JS errors related to cart
  window.addEventListener('error', function (e) {
    var msg = (e.message || '').toLowerCase();
    var file = (e.filename || '').toLowerCase();
    if (msg.indexOf('cart') !== -1 || msg.indexOf('quantity') !== -1 ||
        file.indexOf('cart') !== -1 || file.indexOf('volume') !== -1 ||
        file.indexOf('bb-') !== -1) {
      _sendEvent('cart_js_error', {
        message: (e.message || '').substring(0, 200),
        file: (e.filename || '').split('/').pop(),
        line: e.lineno,
        page: location.pathname
      });
    }
  });

  // Watch for user-visible cart error messages in the DOM
  var _started = false;
  function watchCartErrors() {
    var sidebar = document.getElementById('sidebar-cart');
    if (!sidebar || _started) return;
    _started = true;
    // (An earlier MutationObserver was assigned here and never given .observe() — dead code that
    //  made it look like this was watched twice. The debounced observer below is the real one.)
    // Debounced — only check every 2 seconds max
    var _debounce = null;
    var realObserver = new MutationObserver(function () {
      clearTimeout(_debounce);
      _debounce = setTimeout(function () {
        var errorEls = sidebar.querySelectorAll('.error, .cart-error, [data-error-message]');
        for (var i = 0; i < errorEls.length; i++) {
          var text = (errorEls[i].textContent || '').trim();
          if (text && text.length > 5 && text.toLowerCase().indexOf('error') !== -1) {
            _sendEvent('cart_error', {
              endpoint: 'ui_error',
              status: 0,
              message: text.substring(0, 200),
              page: location.pathname
            });
            break;
          }
        }
      }, 2000);
    });
    realObserver.observe(sidebar, { childList: true, subtree: true });
  }

  // Send GA4 event via gtag
  var _sent = {};
  function _sendEvent(name, params) {
    // Dedupe — don't send same error more than once per 30 seconds
    var key = name + '|' + (params.endpoint || '') + '|' + (params.message || '').substring(0, 50);
    var now = Date.now();
    if (_sent[key] && now - _sent[key] < 30000) return;
    _sent[key] = now;

    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
    // Also log to console for debugging
    console.warn('[BB-Cart-Monitor]', name, params);
  }

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartErrors);
  } else {
    watchCartErrors();
  }
})();

/* ===== Affirm in the slide cart =====================================================
   The drawer is re-rendered by fetching templates/cart.drawer.liquid and injecting the HTML.
   Scripts inside injected HTML DO NOT RUN, so this cannot live in the drawer snippet — it
   lives here, where theme.liquid loads it once per page and it survives every re-render.
   affirm-as-low-as is populated ASYNCHRONOUSLY; an un-refreshed node renders as nothing at
   all, which looks exactly like the app not being installed. ================================ */
(function () {
  var last = 0;
  function refresh() {
    var el = document.querySelector(".bb-affirm-cart .affirm-as-low-as");
    if (!el) return;                      /* drawer not rendered, or cart empty */
    if ((el.textContent || "").trim()) return;   /* already filled — do not re-scan */
    try {
      if (window.affirm && window.affirm.ui && window.affirm.ui.refresh) window.affirm.ui.refresh();
    } catch (e) {}
  }
  try {
    new MutationObserver(function () {
      var now = Date.now();
      if (now - last < 300) return;       /* throttle: the drawer mutates a lot */
      last = now;
      refresh();
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", refresh);
  else refresh();
  document.addEventListener("cart:updated", refresh);
  setTimeout(refresh, 1200);
  setTimeout(refresh, 3000);
})();


/* ── BarrierBucks disclosure in the slide cart (owner 2026-10-03) ──────────────────
   ⛔⛔ THE DRAWER IS AJAX-INJECTED. A <script> inside snippets/bb-cart-bucks.liquid runs
      on the first page load only and never after an add-to-cart, so the toggle lives
      here — theme.liquid loads this file once per page — and is DELEGATED off document,
      which means it keeps working on every re-injected copy of the drawer markup.
   ⭐ No per-node binding and no observer: nothing to re-bind, nothing to leak. */
(function () {
  if (window.__bbBucksToggle) return;
  window.__bbBucksToggle = true;
  document.addEventListener("click", function (e) {
    var t = e.target;
    var btn = (t && t.closest) ? t.closest("[data-bb-bucks-toggle]") : null;
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest("[data-bb-bucks]");
    var panel = wrap && wrap.querySelector("[data-bb-bucks-panel]");
    if (!panel) return;
    var open = panel.style.display !== "block";
    panel.style.display = open ? "block" : "none";
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) wrap.setAttribute("data-open", ""); else wrap.removeAttribute("data-open");
  });
})();


/* ── BarrierBucks row, inside EVERY delivery card (owner 2026-10-03) ───────────────
   "slide cart pdp needs the barrier bucks added in and then remove it from bottom of
   cart" — so the row goes inside the card on BOTH surfaces and the standalone line at
   the foot of the drawer is gone.

   ⛔⛔ WHY IT LIVES HERE AND NOT IN A SNIPPET: the slide cart is AJAX-injected — the
      theme fetches templates/cart.drawer.liquid and sets innerHTML, and a <script>
      inside injected HTML NEVER executes. theme.liquid loads this file once per page,
      so it is the only place that can serve the drawer. There are TWO cards —
      #bb-ship-est-product and #bb-ship-est-cart — and this handles both from one
      implementation rather than two that drift.
   ⛔ SEARCH INSIDE THE CARD, NEVER FROM ITS PARENT. Scoping to card.parentNode put the
      row OUTSIDE the card's border on the PDP ("seems off and out of pdp"). Every
      anchor below is resolved with card.querySelector and re-checked with contains().
   ⭐ Anchoring on the delivery-reviews row satisfies both asks at once: above the
      reviews, and under Fall Rush — Fall Rush already sits above them.
   ⚠️ The estimator REBUILDS each card on any geo or cart change, which takes the row
      with it; the observer in this file puts it back, and insertion is idempotent by id.
   ⭐ The figure is CART-based so it agrees to the cent with what the cart shows — a
      per-variant number read as broken arithmetic beside the card's cart totals. */
(function () {
  if (window.__bbBucksRow) return;
  window.__bbBucksRow = true;

  var FMT = (window.theme && window.theme.moneyFormat) || '${{amount}}';
  var cartBase = null;          /* cents, excluding sample lines; null until fetched */
  var fetching = false;

  function fmt(cents) {
    function pad(n, w) { n = String(n); while (n.length < w) n = '0' + n; return n; }
    function thou(s) { return s.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,'); }
    function amt(precision, thousands, decimal) {
      var n = (cents / 100).toFixed(precision).split('.');
      return thou(n[0]).split(',').join(thousands) + (precision ? decimal + pad(n[1], precision) : '');
    }
    return FMT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      if (name === 'amount') return amt(2, ',', '.');
      if (name === 'amount_no_decimals') return amt(0, ',', '.');
      if (name === 'amount_with_comma_separator') return amt(2, '.', ',');
      if (name === 'amount_no_decimals_with_comma_separator') return amt(0, '.', ',');
      if (name === 'amount_with_space_separator') return amt(2, ' ', ',');
      if (name === 'amount_no_decimals_with_space_separator') return amt(0, ' ', ',');
      if (name === 'amount_with_apostrophe_separator') return amt(2, "'", '.');
      return m;
    });
  }

  /* ⭐ EMPTY CART STILL EARNS A NUMBER ON A PDP. Cart-based is right inside a card whose
     other figures are the cart, but with an empty cart that rendered NOTHING — a first
     visit to a product page showed no row at all. snippets/bb-pdp-bucks.liquid publishes
     the variant prices, and they are used only while the cart is empty. */
  function pdpBase() {
    var P = window.BB_PDP_PRICES;
    if (!P) return 0;
    var form = document.querySelector("form[action*='/cart/add']");
    var i = form && form.querySelector("[name='id']");
    var p = i ? P[String(i.value)] : null;
    if (typeof p !== "number") p = window.BB_PDP_FALLBACK || 0;
    var q = form && form.querySelector("[name='quantity']");
    var n = q ? parseInt(q.value, 10) : 1;
    if (!(n > 0 && n < 10000)) n = 1;
    return p * n;
  }

  function baseCents() { return cartBase ? cartBase : pdpBase(); }

  function bucks() {
    var base = baseCents();          /* NOT named cartBase — that would shadow the outer */
    if (!base) return null;
    /* Liquid's divided_by FLOORS on integers — match snippets/bb-cart-bucks.liquid, or
       this reads a cent above the cart on odd prices. */
    var b = Math.floor(base * 5 / 100);
    return b > 50000 ? 50000 : b;
  }

  function readCart() {
    if (fetching) return;
    fetching = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        fetching = false;
        if (!c) return;
        var sample = 0;
        (c.items || []).forEach(function (li) {
          /* samples are already fully credited — the same exclusion the cart snippet
             makes via properties._src_handle */
          if (li.properties && li.properties._src_handle) sample += li.final_line_price;
        });
        var base = (c.total_price || 0) - sample;
        cartBase = base > 0 ? base : 0;
        place();
        sync();
      })
      .catch(function () { fetching = false; });
  }

  /* ⛔ THE TICK NEVER RE-READ THE CART (owner 2026-10-04: "I adjust cart quantity and the
     values are changing but the BarrierBucks isn't … I have to leave the cart and come back").
     readCart() ran only on boot and on cart:updated / cart:refresh, which this theme's quantity
     stepper does not fire, so sync() kept recomputing from a STALE cartBase.
     ⚠️ Throttled: the MutationObserver debounces 150ms, but a drawer re-render is a mutation
        storm and every tick would otherwise hit /cart.js. readCart() also has its own
        in-flight guard. */
  var lastRead = 0;
  function readCartThrottled() {
    var now = (Date.now ? Date.now() : +new Date());
    if (now - lastRead < 500) return;
    lastRead = now;
    readCart();
  }
  function onCartInput() { readCartThrottled(); sync(); }

  function buildRow(id) {
    /* ⛔ SCRAPPED 2026-11-01 (owner: "scrap barrierbucks" — on the PDP). An empty DocumentFragment
       inserts nothing and is accepted by both appendChild and insertBefore, so all five call sites
       keep working. Returning null would throw at four of them; none null-check.
       ⚠️ Storefront only — the award engine, existing credit and /pages/barrierbucks are untouched. */
    return document.createDocumentFragment();
    var row = document.createElement('div');
    row.className = 'bb-bbrow';
    row.id = id;
    row.setAttribute('data-bb-bucks', '');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bb-bbrow__btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('data-bb-bucks-toggle', '');
    btn.innerHTML = '💰 ' +
                    '<span data-bb-bucks-live>You’re earning <strong data-bb-bucks-amt></strong> in BarrierBucks</span>' +
                    '<span data-bb-bucks-none>Earn <strong>5% back</strong> in BarrierBucks</span> ' +
                    '<span class="bb-bbrow__caret">▾</span>';

    var panel = document.createElement('div');
    panel.className = 'bb-bbrow__panel';
    panel.setAttribute('data-bb-bucks-panel', '');
    panel.innerHTML =
      '<p class="bb-bbrow__lead" data-bb-bucks-live>You’re earning <strong data-bb-bucks-amt></strong> in BarrierBucks</p>' +
      '<p class="bb-bbrow__lead" data-bb-bucks-none>Your cart is empty — add a panel and watch this grow.</p>' +
      '<p><strong>5% back on every order</strong>, as store credit. Nothing to sign up for.</p>' +
      '<p><strong>Added automatically when your order ships</strong> &mdash; no code to enter.</p>' +
      '<p>Spend it like cash on anything we make, up to $500 per order.</p>';

    row.appendChild(btn);
    row.appendChild(panel);
    return row;
  }

  /* the Fall Rush row carries 5px more bottom padding than its neighbours (39px tall
     against 34px), which read as a gap under it. Measured on the live card, not guessed.
     ⚠️ Its padding is set with !important by the theme, so an ordinary inline style
     loses — setProperty with "important" is what actually wins. */
  function evenOutFallRush(card) {
    var peak = card.querySelector('.bb-peak-collapse');
    var peakRow = peak && peak.parentNode;
    if (!peakRow || peakRow.getAttribute('data-bb-evened') === '1') return;
    peakRow.style.setProperty('padding-bottom', '0', 'important');
    peakRow.setAttribute('data-bb-evened', '1');
  }





  var BAND_ON = (function () {
    try {
      if (/[?&]bbband=1/.test(location.search)) { sessionStorage.setItem('bbband', '1'); return true; }
      if (/[?&]bbband=0/.test(location.search)) { sessionStorage.removeItem('bbband'); return false; }
      return sessionStorage.getItem('bbband') === '1';
    } catch (e) { return /[?&]bbband=1/.test(location.search); }
  })();

  /* ── Fall Rush · BarrierBucks · Reviews in ONE row ─────────────────────────────────
     Modelled on the row that already works in this card: "Save on Product · Save on
     Freight · Get it Quicker" (.bb-ship-allin__badges) — one centred flex row, small
     labels, nowrap.

     ⭐ THE FIX FOR THE RACE. Owner's diagnosis was right: the reviews row is built
        DIFFERENTLY from the other two. Fall Rush is built in place by the estimator;
        BarrierBucks is injected here; Reviews is rendered in Liquid somewhere else and
        RELOCATED at runtime by park() in bb-delivery-rating-badge.liquid. Three writers,
        three destinations, three different moments — which is why arranging them from
        the outside kept coming apart.
        Now there is ONE destination, rendered server-side by
        snippets/bb-shipping-estimator.liquid: <div data-bb-rowbar>. The estimator's row
        is moved in once, park() delivers the reviews row into it, and this file puts
        BarrierBucks there. Nobody re-homes anybody.
     ⭐ ORDER IS CSS, NOT DOM. Each cell is tagged and ordered with `order:`, so whoever
        arrives last cannot disturb the sequence — the previous versions depended on
        insertion order and broke whenever a component rebuilt. */
  var ROWBAR_CSS =
    '[data-bb-rowbar]{display:flex;flex-wrap:wrap;align-items:stretch;justify-content:center;' +
      'border-top:1px solid rgba(0,0,0,.06)}' +
    '[data-bb-rowbar]:empty{display:none;border-top:0}' +
    '[data-bb-rowbar] > *{flex:1 1 0 !important;width:33.333% !important;max-width:33.333% !important;' +
      'min-width:0 !important;box-sizing:border-box !important;margin:0 !important;' +
      'border-top:0 !important;border-right:1px solid rgba(0,0,0,.06);background:none !important}' +
    '[data-bb-rowbar] > [data-bb-cell="fall"]{order:1}' +
    '[data-bb-rowbar] > [data-bb-cell="bucks"]{order:2}' +
    '[data-bb-rowbar] > [data-bb-cell="reviews"]{order:3;border-right:0}' +
    /* an OPEN cell takes the whole bar and its panel pushes the card down — absolute
       panels were clipped inside the slide cart */
    '[data-bb-rowbar] > [data-bb-open]{flex:1 1 100% !important;width:100% !important;' +
      'max-width:100% !important;border-right:0;order:4}' +
    /* ⛔ SPECIFICITY: .bb-drs--attached .bb-drs__toggle (0,2,0) and
       .bb-ship-trust button[aria-expanded] (0,2,1) both beat a bare descendant rule,
       which is why the three cells sat at different heights. Match them. */
    '[data-bb-rowbar] button,[data-bb-rowbar] .bb-drs--attached .bb-drs__toggle,' +
      '[data-bb-rowbar] button[aria-expanded]{display:flex !important;align-items:center !important;' +
      'justify-content:center !important;gap:4px !important;width:100% !important;' +
      'box-sizing:border-box !important;margin:0 !important;padding:9px 4px !important;' +
      'font-size:10px !important;letter-spacing:.3px;line-height:1.2 !important;white-space:nowrap}' +
    /* a tap on mobile leaves :hover stuck — that was the odd peach cell, not a style */
    '[data-bb-rowbar] button:hover{background:none !important}' +
    '[data-bb-rowbar] .bb-bbrow__caret,[data-bb-rowbar] [data-bb-caret],' +
      '[data-bb-rowbar] .bb-drs__caret{position:static !important;transform:none !important;' +
      'font-size:7px;opacity:.45;margin:0}' +
    '[data-bb-rowbar] .bb-peak-collapse,[data-bb-rowbar] .bb-bbrow__panel,' +
      '[data-bb-rowbar] .bb-drs__panel{border-top:1px solid rgba(0,0,0,.06);padding:8px 12px;' +
      'text-align:center}' +
    '@media (max-width:600px){[data-bb-rowbar] button{font-size:9.5px !important;padding:8px 2px !important}}';

  function rowbarCss() {
    if (document.getElementById('bb-rowbar-css')) return;
    var s = document.createElement('style');
    s.id = 'bb-rowbar-css';
    s.textContent = ROWBAR_CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  /* one line per cell: icon + name + caret. The detail stays inside each panel. */
  function label(row, kind) {
    var btn = row.querySelector('button');
    if (!btn || btn.getAttribute('data-bb-labelled') === '1') return;
    var caret = btn.querySelector('.bb-bbrow__caret,[data-bb-caret],.bb-drs__caret');
    var ic = kind === 'bucks' ? '💰' : kind === 'reviews' ? '★' : '🍂';
    var nm = kind === 'bucks' ? 'BarrierBucks' : kind === 'reviews' ? 'Reviews' : 'Fall Rush';
    btn.innerHTML = '<span class="bb-rowbar__ic">' + ic + '</span>' +
                    '<span class="bb-rowbar__n">' + nm + '</span>';
    if (caret) btn.appendChild(caret);
    btn.setAttribute('data-bb-labelled', '1');
  }

  function kindOf(el) {
    if (el.classList.contains('bb-bbrow')) return 'bucks';
    if (el.classList.contains('bb-drs')) return 'reviews';
    if (el.querySelector && el.querySelector('.bb-peak-collapse')) return 'fall';
    return null;
  }

  function rowbarOf(card) { return card.querySelector('[data-bb-rowbar]'); }

  function band(card) {
    if (!BAND_ON) return;
    var bar = rowbarOf(card);
    if (!bar) return;                       /* older theme render — leave the card alone */
    rowbarCss();

    /* the estimator builds Fall Rush inside .bb-ship-trust; move that row in once */
    var peak = card.querySelector('.bb-peak-collapse');
    var fall = peak && peak.parentNode;
    if (fall && fall.parentNode !== bar && card.contains(fall)) bar.appendChild(fall);

    /* tag + label every cell, and drop anything stale a rebuild left behind */
    var seen = {}, kids = [].slice.call(bar.children);
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i], kind = kindOf(k);
      if (!kind || seen[kind]) { if (k.parentNode) k.parentNode.removeChild(k); continue; }
      seen[kind] = true;
      k.setAttribute('data-bb-cell', kind);
      label(k, kind);
    }
  }

  /* an open cell spans the bar; re-read after each component's own toggle has run */
  function bandOpen() {
    var bars = document.querySelectorAll('[data-bb-rowbar]');
    for (var b = 0; b < bars.length; b++) {
      var kids = bars[b].children;
      for (var j = 0; j < kids.length; j++) {
        var pn = kids[j].querySelector('.bb-peak-collapse,.bb-bbrow__panel,.bb-drs__panel');
        var open = pn && getComputedStyle(pn).display !== 'none';
        if (open) kids[j].setAttribute('data-bb-open', '');
        else kids[j].removeAttribute('data-bb-open');
      }
    }
  }

  /* ── SLIDE CART: the three rows live BELOW Share My Cart ─────────────────────────
     Owner 2026-10-03: Fall Rush, Delivery Reviews and BarrierBucks all move under the
     share-cart row and come OUT of the drawer's delivery card. The PDP card is untouched.
     ⛔ park() in bb-delivery-rating-badge.liquid is what owns the reviews row. It has been
        taught to treat [data-bb-cartgroup] as that row's home when the panel is the CART
        estimator — without that it drags the row straight back into the card and the
        group loses a third of itself a second later.
     ⚠️ The drawer is AJAX-injected and rebuilt on every cart change, so this re-runs from
        the observer; it is idempotent via the container marker and the dedupe below. */
  /* ⭐ injected at BOOT, not on the first move. The rules that hide the Fall Rush row while
     it is still inside the cart card must exist BEFORE the drawer first renders, or the row
     paints once in the card and vanishes when cartGroup() moves it — reported as a strip
     popping up and disappearing on first open. */
  function cartGroupCss() {
    if (document.getElementById('bb-cartgroup-css')) return;
    var st = document.createElement('style');
    st.id = 'bb-cartgroup-css';
      st.textContent = ".bb-cartgroup{display:flex;flex-direction:column;border-top:1px solid rgba(0,0,0,.07);margin:0}.bb-cartgroup > *{width:100% !important;max-width:100% !important;margin:0 !important;padding:0 !important;border:0 !important;border-top:1px solid rgba(0,0,0,.07) !important;background:none !important;border-radius:0 !important;box-shadow:none !important;display:block !important}.bb-cartgroup > [data-bb-cell=\"fall\"]{order:1;border-top:0 !important}.bb-cartgroup > [data-bb-cell=\"bucks\"]{order:2}.bb-cartgroup > [data-bb-cell=\"reviews\"]{order:3}.bb-cartgroup .bb-bbrow__btn,.bb-cartgroup .bb-drs__toggle,.bb-cartgroup > * > button[aria-expanded]{position:relative !important;display:flex !important;align-items:center !important;justify-content:center !important;gap:6px !important;width:100% !important;box-sizing:border-box !important;margin:0 !important;padding:9px 30px !important;font-size:12.5px !important;font-weight:600 !important;line-height:1.25 !important;background:none !important;text-align:center !important;white-space:nowrap}.bb-cartgroup .bb-bbrow__btn:hover,.bb-cartgroup .bb-drs__toggle:hover{background:none !important}.bb-cartgroup .bb-bbrow__caret,.bb-cartgroup [data-bb-caret],.bb-cartgroup .bb-drs__caret{position:absolute !important;right:12px !important;top:50% !important;transform:translateY(-50%) !important;font-size:9px !important;opacity:.4 !important;margin:0 !important;pointer-events:none !important}.bb-cartgroup > *{cursor:pointer}.bb-cartgroup .bb-bbrow__btn,.bb-cartgroup .bb-drs__toggle,.bb-cartgroup > * > button[aria-expanded]{min-height:40px !important;cursor:pointer !important}.bb-cartgroup [data-open] .bb-drs__caret{transform:translateY(-50%) rotate(180deg) !important}.bb-cartgroup .bb-drs__stars{font-size:.78rem !important;letter-spacing:.4px !important}.bb-cartgroup .bb-peak-collapse,.bb-cartgroup .bb-bbrow__panel{border-top:1px solid rgba(0,0,0,.06) !important;padding:7px 14px 8px !important;text-align:center !important;font-size:11.5px !important;line-height:1.4 !important}.bb-cartgroup .bb-peak-collapse > div{margin:1px 0 !important}.bb-cartgroup .bb-bbrow__panel p{margin:1px 0 !important}.bb-cartgroup .bb-drs__panel{border-top:1px solid rgba(0,0,0,.06) !important;padding:6px 28px 7px !important;text-align:center !important}.bb-cartgroup .bb-drs__wrap{position:relative !important;margin:0 !important;padding:0 !important;border:0 !important}.bb-cartgroup .bb-drs__rail,.bb-cartgroup .bb-drs__slide{min-height:0 !important;height:auto !important;margin:0 !important;padding:0 !important}.bb-cartgroup .bb-drs__q{margin:0 !important;font-size:12px !important;line-height:1.35 !important}.bb-cartgroup .bb-drs__by{margin:1px 0 0 !important;font-size:11px !important;line-height:1.3 !important}.bb-cartgroup .bb-drs__nav{position:absolute !important;top:50% !important;transform:translateY(-50%) !important;display:inline-flex !important;align-items:center !important;justify-content:center !important;width:22px !important;height:22px !important;min-width:0 !important;padding:0 !important;margin:0 !important;border-radius:50% !important;font-size:12px !important;line-height:1 !important;opacity:.5}.bb-cartgroup .bb-drs__nav--prev{left:2px !important;right:auto !important}.bb-cartgroup .bb-drs__nav--next{right:2px !important;left:auto !important}.bb-cartgroup .bb-drs__dots{padding:3px 0 0 !important;margin:0 !important}.bb-cartgroup .bb-drs__hd,.bb-cartgroup .bb-drs__sub,.bb-cartgroup .bb-drs__rated,.bb-cartgroup .bb-drs__based{margin:0 !important}#bb-ship-est-cart .bb-ship-trust > div:has(> .bb-peak-collapse){display:none !important}#bb-ship-est-cart > .bb-drs,#bb-ship-est-cart .bb-ship-trust > .bb-drs{display:none !important}[data-bb-rowbar]:empty{display:none !important}.bb-ship-est--cart{margin-bottom:10px !important}#bb-ship-est-cart .bb-ship-allin__badges{margin-top:5px !important}#bb-ship-est-cart .bb-ship-trust{padding-bottom:0 !important}#bb-ship-est-cart .bb-ship-trust > div:not(.bb-ship-allin__badges){padding-top:2px !important;padding-bottom:3px !important}#bb-ship-est-cart .bb-ship-allin__label:has(strong){padding:7px 6px !important;margin:5px -9px 2px !important;width:auto !important;max-width:none !important}#bb-ship-est-cart .bb-ship-trust > div:has(.bb-ship-allin__label){padding-left:4px !important;padding-right:4px !important}#bb-ship-est-cart .bb-ship-delivery{padding-top:3px !important;padding-bottom:5px !important}#bb-ship-est-cart{padding-bottom:0 !important}#bb-ship-est-cart > *:last-child{margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-trust > div:last-child{padding-bottom:2px !important;margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-delivery:last-child{padding-bottom:4px !important}#bb-ship-est-cart [data-bb-rowbar]{display:none !important}#bb-ship-est-cart .bb-ship-trust{padding-bottom:0 !important;margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-trust > div:last-child{padding-bottom:0 !important;margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-allin__label:has(strong){margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-allin__badges:last-child{padding-bottom:4px !important;margin-bottom:0 !important}#bb-ship-est-cart .bb-ship-trust > div:empty{display:none !important}#bb-ship-est-cart .bb-ship-trust > div:not(:has(*)):not(:has(img)){padding:0 !important}#bb-ship-est-cart .bb-ship-status:empty,#bb-ship-est-cart .bb-ship-delivery:empty{display:none !important}#bb-ship-est-cart .bb-ship-slider[style*='display: none'],#bb-ship-est-cart .bb-ship-slider[style*='display:none']{display:none !important}#bb-ship-est-cart .bb-ship-slider,#bb-ship-est-cart .bb-ship-delivery,#bb-ship-est-cart .bb-ship-trust,#bb-ship-est-cart .bb-ship-rowbar{display:none !important}#bb-ship-est-cart .bb-ship-header{padding-bottom:2px !important}#bb-ship-est-cart .bb-ship-header > *:last-child{margin-bottom:0 !important}[data-bb-pdpfall]{border:1.5px solid #EB673C;border-radius:8px;background:#fff;margin:0 0 12px;overflow:hidden}[data-bb-pdpfall] .bb-bbrow__btn{position:relative;display:flex;align-items:center;justify-content:center;gap:6px;width:100%;box-sizing:border-box;margin:0;padding:11px 30px;border:0;background:none;font:inherit;font-size:13px;font-weight:600;line-height:1.25;text-align:center;cursor:pointer}[data-bb-pdpfall] .bb-bbrow__caret{position:absolute;right:12px;top:50%;transform:translateY(-50%);font-size:9px;opacity:.4;pointer-events:none}[data-bb-pdpfall] .bb-bbrow__panel{border-top:1px solid rgba(0,0,0,.06);padding:8px 14px 10px;text-align:center;font-size:11.5px;line-height:1.4}[data-bb-pdpfall] .bb-bbrow__panel p{margin:2px 0}[data-bb-pdpbucks]{border:1.5px solid #EB673C;border-radius:8px;background:#fff;margin:0 0 12px;overflow:hidden}[data-bb-pdpbucks] .bb-bbrow{border-top:0 !important;text-align:center}[data-bb-pdpbucks] .bb-bbrow__btn{padding:11px 30px !important;font-size:13px !important}[data-bb-pdpbucks] .bb-bbrow__panel{border-top:1px solid rgba(0,0,0,.06);padding:8px 14px 10px;text-align:center;font-size:11.5px;line-height:1.4}[data-bb-pdpbucks] .bb-bbrow__panel p{margin:2px 0}[data-bb-bucks-none]{display:none}.bb-bbrow[data-bb-empty] [data-bb-bucks-live]{display:none}.bb-bbrow[data-bb-empty] [data-bb-bucks-none]{display:inline}.bb-bbrow[data-bb-empty] p[data-bb-bucks-none]{display:block}";
    (document.head || document.documentElement).appendChild(st);
  }


  /* ── Fall Rush before a zip/postal is known ────────────────────────────────────────────
     The estimator only builds its Fall Rush row once geo resolves, because its copy names the
     town ("the next truck to Beverly Hills"). With no zip there is nothing to move, so the
     strip was simply missing — on CA it was missing entirely until a postal was entered.
     ⭐ This stands in with the SAME headline and the same two sentences, minus the two things
        we do not know yet: the town, and "the window above" (there is no window on screen
        without geo). Nothing here is invented.
     ⛔ SEASON GATE. _peakMonths is hardcoded in BOTH estimator assets
        (bb-shipping-estimator.js and -ca.js) as Apr–Nov, 0-indexed [3..10]; this is a THIRD
        copy and must be changed with them or the three drift.
     ⭐ Replaced the moment the real row arrives — band()/cartGroup() dedupe by kind, and this
        one is removed explicitly so the geo-aware copy always wins. */
  var PEAK_MONTHS = [] /* Fall Rush RETIRED 2026-11-01 — unbacked scarcity. Refill to revive. */;
  function inPeak() {
    try { return PEAK_MONTHS.indexOf(new Date().getMonth()) !== -1; } catch (e) { return false; }
  }
  function fallRushStandin(host) {
    if (!host) return;
    var real = host.querySelector('[data-bb-cell="fall"]:not([data-bb-fallstandin])');
    var mine = host.querySelector('[data-bb-fallstandin]');
    /* the geo-aware row has arrived — drop the stand-in */
    if (real) { if (mine) mine.parentNode.removeChild(mine); return; }
    /* ⛔ SERVICEABILITY GATE. Without this the stand-in is immortal in a paused region: the
       estimator never builds a real Fall Rush row there, so `real` is null forever and a
       shopper we have stopped serving is told spots are filling fast. The estimator writes
       #bb-ship-status-<ctx> in its unserved/crane-blocked branch ONLY and clears it
       everywhere else, so a non-empty status means paused or never-served. Remove, do not
       merely skip - the row may already be on screen when the notice appears. */
    var _bbNotice = (function () {
      var ids = ['bb-ship-status-cart', 'bb-ship-status-product', 'bb-ship-status-cartpage'];
      for (var i = 0; i < ids.length; i++) {
        var e = document.getElementById(ids[i]);
        if (e && e.innerHTML.replace(/\s|&nbsp;/g, '') !== '') return true;
      }
      return false;
    })();
    if (_bbNotice) { if (mine && mine.parentNode) mine.parentNode.removeChild(mine); return; }
    /* ⛔ THE STAND-IN IS BUILT ONCE AND EVERY LATER TICK RETURNS PAST IT, so one built before
       the estimator published window.BB_LOCALE keeps the town-less sentence for the whole
       visit. Measured on CA: BB_LOCALE was "Vancouver" and the PDP row read "to Vancouver"
       while this row still read "the next truck". Refresh in place rather than skip. */
    if (mine) {
      try {
        var _lc = String(window.BB_LOCALE || '').replace(/[^A-Za-z .'-]/g, '').trim().slice(0, 40);
        var _pn = mine.querySelector('.bb-peak-collapse');
        if (_lc && _pn && _pn.innerHTML.indexOf(' to ' + _lc + ' ') === -1) {
          _pn.innerHTML =
            '<p>Spots on the next truck to ' + _lc + ' are filling fast &mdash; most orders ship earlier than the ' +
            'window we quote.</p>' +
            '<p>Book your installer only after shipment confirmation.</p>';
        }
      } catch (e) {}
    }
    if (mine || !inPeak()) return;

    var row = document.createElement('div');
    row.setAttribute('data-bb-fallstandin', '');
    row.setAttribute('data-bb-cell', 'fall');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.className = 'bb-bbrow__btn';
    btn.innerHTML = '🍂 Fall Rush — Reserve Your Spot. ' +
                    '<span class="bb-bbrow__caret">▾</span>';

    var panel = document.createElement('div');
    /* ⛔ MUST carry bb-peak-collapse. kindOf() in the group identifies a Fall Rush row by that
       class; without it the row came back as an unknown kind and the dedupe deleted the
       stand-in on the very tick that created it — which is why it never appeared. */
    panel.className = 'bb-bbrow__panel bb-peak-collapse';
    /* ⭐ name the town when the estimator has published one; otherwise this is byte-for-byte
       the sentence that shipped before, so nothing is invented when geo is unknown.
       ⛔ WHITELISTED - this value reaches innerHTML and originates in a ZIP lookup. */
    var _bbLoc = '';
    try { _bbLoc = String(window.BB_LOCALE || '').replace(/[^A-Za-z .'-]/g, '').trim().slice(0, 40); } catch (e) { _bbLoc = ''; }
    panel.innerHTML =
      '<p>Spots on the next truck' + (_bbLoc ? ' to ' + _bbLoc : '') + ' are filling fast &mdash; most orders ship earlier than the ' +
      'window we quote.</p>' +
      '<p>Book your installer only after shipment confirmation.</p>';

    btn.addEventListener('click', function () {
      var open = panel.style.display !== 'block';
      panel.style.display = open ? 'block' : 'none';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) row.setAttribute('data-open', ''); else row.removeAttribute('data-open');
    });

    panel.style.display = 'none';
    row.appendChild(btn);
    row.appendChild(panel);
    host.appendChild(row);
  }

  function cartGroup() {
    /* ⭐ BELOW SHARE MY CART (owner 2026-10-04, after trying it inside the card). The host is
       created once directly after the share row and kept there; park() has a matching branch
       that treats [data-bb-cartgroup] as the reviews row's home for the CART estimator. */
    var share = document.querySelector('.bb-share-cart-row');
    /* ⛔ THE CARD IS OPTIONAL. On CA with no postal the drawer renders NO delivery card at
       all, so requiring it meant the whole group never appeared — the owner saw nothing until
       a postal code was entered, then everything arrived at once. The group only needs the
       share row; the three rows are collected if and when they exist. */
    var card = document.getElementById('bb-ship-est-cart');
    if (!share || !share.parentNode) return;
    var host = document.querySelector('[data-bb-cartgroup]');
    if (!host) {
      host = document.createElement('div');
      host.setAttribute('data-bb-cartgroup', '');
      host.className = 'bb-cartgroup';
      share.parentNode.insertBefore(host, share.nextSibling);
    } else if (host.previousElementSibling !== share && share.parentNode === host.parentNode) {
      share.parentNode.insertBefore(host, share.nextSibling);   /* stay put under share */
    }
    var peak = card && card.querySelector('.bb-peak-collapse');
    var fall = peak && peak.parentNode;
    if (fall && card && card.contains(fall)) host.appendChild(fall);
    var bucks = card && card.querySelector('.bb-bbrow');
    if (bucks && card.contains(bucks)) host.appendChild(bucks);
    /* ⛔ THE REVIEWS ROW IS NOT ALWAYS IN THE CARD. With no zip/postal the estimator is
       unresolved, so park() takes its NOT-LIVE branch and returns the row to its original
       home near the top of the drawer — it then rendered above the line items instead of
       in this group. Collect it from anywhere INSIDE THE DRAWER.
       ⚠️ Scoped to the drawer on purpose: the PDP has its own .bb-drs--attached and must
       never have it stolen. */
    var scope = (card || share).closest('.Drawer, .cart-drawer, .Cart-Drawer, [data-cart-drawer]') ||
                (card || share).parentNode;
    var rev = (card && card.querySelector('.bb-drs')) ||
              (scope && scope.querySelector('.bb-drs--attached'));
    if (rev && scope && scope.contains(rev) && rev.parentNode !== host) host.appendChild(rev);
    /* ⭐ BARRIERBUCKS DOES NOT DEPEND ON THE CARD. With no zip/postal the estimator never
       builds its rows, so place() had nothing to anchor to and the group came up empty
       below Share My Cart. Build the row straight into the group instead.
       ⛔ Stable id + getElementById check — the same duplicate guard place() uses; without
          one, every observer tick appends another row. */
    fallRushStandin(host);
    if (!host.querySelector('.bb-bbrow') && !document.getElementById('bb-bbrow-cartgroup')) {
      host.appendChild(buildRow('bb-bbrow-cartgroup'));
      sync();
    }

    /* ⛔ one of each kind — a rebuild leaves the old node behind and they stack up */
    var seen = {}, kids = [].slice.call(host.children);
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      var kind = k.classList.contains('bb-bbrow') ? 'bucks'
               : k.classList.contains('bb-drs') ? 'reviews'
               : (k.querySelector && k.querySelector('.bb-peak-collapse')) ? 'fall' : null;
      if (!kind || seen[kind]) { if (k.parentNode) k.parentNode.removeChild(k); continue; }
      seen[kind] = true;
      /* ⭐ ORDER IS CSS, NOT DOM: Fall Rush, BarrierBucks, Reviews — whoever arrives last
         (park() delivers the reviews row on its own schedule) cannot change the sequence. */
      k.setAttribute('data-bb-cell', kind);
    }
  }

  function place() {
    /* ⛔⛔ THE MIXED-TRUCK NOTICE IS SERVER-RENDERED LIQUID (snippets/cart-items.liquid,
       [data-bb-mixnote]) and cannot know whether we serve the address. Measured in the CA
       slide cart at a Toronto postal: directly under "New orders to Ontario are paused" it
       still read "This order ships on two trucks - 2 Light items $149 Dec 3-23, 12 Heavy
       items $199 Dec 3-23" - prices and dates quoted into a province we have closed.
       Same signal as the Fall Rush gate: a non-empty status element IS a notice. */
    try {
      var _note = false, _nids = ['bb-ship-status-cart', 'bb-ship-status-product', 'bb-ship-status-cartpage'];
      for (var _n = 0; _n < _nids.length; _n++) {
        var _ne = document.getElementById(_nids[_n]);
        if (_ne && _ne.innerHTML.replace(/\s|&nbsp;/g, '') !== '') { _note = true; break; }
      }
      var _mx = document.querySelectorAll('[data-bb-mixnote]');
      for (var _m = 0; _m < _mx.length; _m++) _mx[_m].style.display = _note ? 'none' : '';
    } catch (e) {}
    /* ⭐ NO baseCents() GATE. The row carries its own empty state now, so an empty cart
       gets the "earn 5% back" invitation instead of a missing row. */
    var cards = document.querySelectorAll('[id^="bb-ship-est"]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var id = 'bb-bbrow-' + (card.id || i);
      /* ⛔⛔ THE DUPLICATE GUARD. Without this every observer tick appends ANOTHER row —
         it was deleted by accident during a revert and the card filled with hundreds of
         identical BarrierBucks lines. Never remove it. */
      if (document.getElementById(id)) { evenOutFallRush(card); band(card); continue; }
      /* ⭐ the server-rendered bar is the one destination — use it when present */
      /* ⛔ GATED TOO. Without BAND_ON here the row moved into the bar for customers as
         well, which silently changed the live card — the flag has to cover the
         destination, not just the styling. */
      var bar = BAND_ON ? card.querySelector('[data-bb-rowbar]') : null;
      if (bar) { bar.appendChild(buildRow(id)); evenOutFallRush(card); band(card); continue; }
      var drs = card.querySelector('.bb-drs');
      if (drs && drs.parentNode && card.contains(drs.parentNode)) {
        drs.parentNode.insertBefore(buildRow(id), drs);
        evenOutFallRush(card);
        band(card);
        continue;
      }
      var peak = card.querySelector('.bb-peak-collapse');
      var anchor = (peak && peak.parentNode && card.contains(peak.parentNode)) ? peak.parentNode : null;
      if (anchor && anchor.parentNode && card.contains(anchor.parentNode)) {
        anchor.parentNode.insertBefore(buildRow(id), anchor.nextSibling);
        evenOutFallRush(card);
        band(card);
      }
    }
  }

  function sync() {
    /* ⛔ EMPTINESS IS A CART FACT, NOT A FIGURE FACT (owner 2026-10-04). baseCents() is
       `cartBase ? cartBase : pdpBase()`, so on a PRODUCT PAGE with an empty cart bucks() still
       returned a real number — 5% of the selected variant at quantity 1 — and the row read
       "$12.49" with nothing in the cart. cartBase is null until /cart.js answers and that
       counts as empty, so a wrong figure never shows even for a moment. */
    var v = cartBase ? bucks() : null;
    /* ⛔ THE MODE FLIP MUST HAPPEN BEFORE THE EARLY RETURN. This used to `return` on null
       without touching the DOM, so emptying the cart left the PREVIOUS figure on screen
       until something else re-rendered the row. */
    var rows = document.querySelectorAll('[data-bb-bucks]');
    for (var r = 0; r < rows.length; r++) {
      if (v === null) rows[r].setAttribute('data-bb-empty', '');
      else rows[r].removeAttribute('data-bb-empty');
    }
    if (v === null) return;
    var nodes = document.querySelectorAll('[data-bb-bucks-amt]');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = fmt(v);
  }

  var css = document.createElement('style');
  css.textContent =
    /* copied from the two rows it sits between — full-bleed button, centred label,
       caret absolutely positioned at right:14px so it lines up across every row */
    '.bb-bbrow{border-top:1px solid rgba(0,0,0,.06);text-align:center}' +
    '.bb-bbrow__btn{position:relative;display:flex;width:100%;box-sizing:border-box;' +
      'align-items:center;justify-content:center;gap:8px;border:0;background:transparent;' +
      'color:inherit;font:inherit;font-size:13px;font-weight:600;padding:7px 26px;margin:0;' +
      'cursor:pointer;text-align:center}' +
    '.bb-bbrow__btn:hover{background:rgba(235,103,60,.05)}' +
    '.bb-bbrow__btn strong{color:#EB673C}' +
    '.bb-bbrow__caret{position:absolute;right:14px;top:50%;transform:translateY(-50%);' +
      'font-size:10px;opacity:.55;display:inline-block;transition:transform .18s}' +
    '.bb-bbrow[data-open] .bb-bbrow__caret{transform:translateY(-50%) rotate(180deg)}' +
    /* matches .bb-peak-collapse: 11px, centred, 2px line gaps */
    '.bb-bbrow__panel{display:none;padding:0 14px 6px;font-size:11px;line-height:1.35;opacity:.9;text-align:center}' +
    '.bb-bbrow__panel p{margin:2px 0}' +
    /* TWO lines, not four: icon + name + caret share line 1, the value sits on line 2.
       Three stacked lines made the band 100px — exactly what the stacked rows cost, so it
       saved nothing. Measured, not guessed. */
      'gap:0 4px;padding:5px 4px !important;width:100% !important;margin:0 !important;line-height:1.25;height:auto}' +
    '.bb-bbrow__lead{font-weight:600;color:#44403c}' +
    '.bb-bbrow__lead strong{color:#EB673C}' +
      'transform:none !important;margin:1px 0 0;font-size:8px;opacity:.5}';

  (document.head || document.documentElement).appendChild(css);


  /* ── Fall Rush on the PDP before a zip is known ───────────────────────────────────────
     ⛔ WHY THIS IS NEEDED AT ALL. renderElement() bails at its first line when geo is
        unresolved (`if (!el || !geoState || !geoZone) return;`), so with no zip the PDP
        estimator card is never built and stays display:none. All the shopper sees is the
        zip prompt and the parked delivery-reviews row — "it shows delivery and nothing
        else" (owner 2026-10-04). The Fall Rush row is a child of that unbuilt card, so it
        cannot simply be moved; it has to be stood in for.
     ⭐ Same headline and same two sentences as the slide-cart stand-in, minus the town and
        "the window above" — neither exists without geo. Nothing is invented.
     ⛔ SEASON GATE shares PEAK_MONTHS with the slide-cart stand-in above, which is itself a
        THIRD copy of _peakMonths in the two estimator assets. Change all three together. */
  function pdpStandinRow() {
    var row = document.createElement('div');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.className = 'bb-bbrow__btn';
    btn.innerHTML = '🍂 Fall Rush — Reserve Your Spot. ' +
                    '<span class="bb-bbrow__caret">▾</span>';
    var panel = document.createElement('div');
    panel.className = 'bb-bbrow__panel';
    panel.style.display = 'none';
    panel.innerHTML =
      '<p>Spots on the next truck are filling fast &mdash; most orders ship earlier than the ' +
      'window we quote.</p>' +
      '<p>Book your installer only after shipment confirmation.</p>';
    btn.addEventListener('click', function () {
      var open = panel.style.display !== 'block';
      panel.style.display = open ? 'block' : 'none';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var c = btn.querySelector('.bb-bbrow__caret');
      if (c) c.textContent = open ? '▴' : '▾';
    });
    row.appendChild(btn);
    row.appendChild(panel);
    return row;
  }

  function pdpStandin() {
    var zip = document.getElementById('bb-ship-zip-product');
    var card = document.getElementById('bb-ship-est-product');
    var fallHost = document.querySelector('[data-bb-pdpfall]');
    var bucksHost = document.querySelector('[data-bb-pdpbucks]');
    /* ⭐ offsetParent, not the style attribute: the estimator toggles BOTH of these with
       inline display, and offsetParent also catches an ancestor being hidden. */
    var zipOn = !!(zip && zip.offsetParent !== null);
    var cardOn = !!(card && card.offsetParent !== null);
    /* the real card has resolved (or there is no prompt) — the geo-aware rows win */
    if (!zipOn || cardOn) {
      if (fallHost && fallHost.parentNode) fallHost.parentNode.removeChild(fallHost);
      if (bucksHost && bucksHost.parentNode) bucksHost.parentNode.removeChild(bucksHost);
      return;
    }
    if (!zip.parentNode) return;
    /* ⛔ SEPARATE GATES. Fall Rush is seasonal (Apr–Nov); BarrierBucks is earned year round.
       One shared gate would have taken BarrierBucks off the page every December. */
    if (inPeak() && !fallHost) {
      fallHost = document.createElement('div');
      fallHost.setAttribute('data-bb-pdpfall', '');
      fallHost.appendChild(pdpStandinRow());
      zip.parentNode.insertBefore(fallHost, zip.nextSibling);
    } else if (!inPeak() && fallHost && fallHost.parentNode) {
      fallHost.parentNode.removeChild(fallHost);
      fallHost = null;
    }
    /* ⭐ BarrierBucks needs NO geo — baseCents() reads the CART, which is the whole reason the
       figure agrees with the slide cart. It sits between Fall Rush and the parked reviews row.
       ⛔ Stable id + the existing-node check is the same duplicate guard place() uses; without
          one, every observer tick appends another row (70 of them went live on 2026-10-03). */
    if (!bucksHost && !document.getElementById('bb-bbrow-pdpstandin')) {
      bucksHost = document.createElement('div');
      bucksHost.setAttribute('data-bb-pdpbucks', '');
      bucksHost.appendChild(buildRow('bb-bbrow-pdpstandin'));
      var after = fallHost || zip;
      after.parentNode.insertBefore(bucksHost, after.nextSibling);
      sync();
    }
  }

  function boot() { cartGroupCss(); readCart(); place(); sync(); cartGroup(); pdpStandin(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* a variant or quantity change only matters while the cart is empty, but it is cheap */
  document.addEventListener('change', onCartInput, true);
  document.addEventListener('input', onCartInput, true);
  document.addEventListener('variant:change', sync);
  document.addEventListener('click', function () { setTimeout(bandOpen, 60); setTimeout(bandOpen, 260); }, true);
  document.addEventListener('cart:updated', readCart);
  document.addEventListener('cart:refresh', readCart);

  var t = null;
  try {
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { readCartThrottled(); place(); sync(); cartGroup(); pdpStandin(); }, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  setTimeout(boot, 1200);
  setTimeout(boot, 3000);
})();
